import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Users, Building2, RefreshCw, GitMerge, RotateCcw, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/*
 * Admin panel for finding and merging duplicate accounts & contacts.
 *
 * Detection is server-side (Levenshtein + shared email/domain/phone signals).
 * Merges are soft — the loser gets a merged_into pointer and its linked rows
 * (deals, contacts, projects, tickets, tasks, documents) are re-parented to
 * the survivor. Everything is written to the merge_audit collection so a merge
 * can be reversed from the "Merged records" tab.
 */
export default function DedupePanel() {
  const [tab, setTab] = useState("accounts");
  return (
    <div className="space-y-4" data-testid="dedupe-panel">
      <div>
        <h2 className="text-lg font-heading font-semibold text-slate-900">Duplicates</h2>
        <p className="text-sm text-slate-500 mt-1">
          Detect and merge near-duplicate accounts and contacts. Every merge re-parents linked
          deals, projects, tickets and tasks to the survivor and writes an audit entry so it can
          be undone from the &quot;Merged records&quot; tab.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="dedupe-tabs">
          <TabsTrigger value="accounts" data-testid="dedupe-tab-accounts">
            <Building2 className="h-3.5 w-3.5 mr-1.5" /> Account duplicates
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="dedupe-tab-contacts">
            <Users className="h-3.5 w-3.5 mr-1.5" /> Contact duplicates
          </TabsTrigger>
          <TabsTrigger value="merged" data-testid="dedupe-tab-merged">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Merged records
          </TabsTrigger>
        </TabsList>
        <TabsContent value="accounts" className="mt-4">
          <DuplicatesList entity="account" />
        </TabsContent>
        <TabsContent value="contacts" className="mt-4">
          <DuplicatesList entity="contact" />
        </TabsContent>
        <TabsContent value="merged" className="mt-4">
          <MergedList />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DuplicatesList({ entity }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const path = entity === "account" ? "/dedupe/accounts/candidates" : "/dedupe/contacts/candidates";
      const r = await api.get(path);
      setGroups(r.data.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Detection failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [entity]);

  const openMerge = (group, survivorId) => {
    const losers = group.members.filter((m) => m.id !== survivorId).map((m) => m.id);
    setPending({ group, survivorId, losers });
    setMergeOpen(true);
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-heading">
          {loading ? "Scanning…" : `${groups.length} duplicate group${groups.length === 1 ? "" : "s"} found`}
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1" data-testid={`dedupe-rescan-${entity}`}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Rescan
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        {groups.length === 0 && !loading && (
          <div className="text-center py-8 text-sm text-slate-500 flex flex-col items-center gap-2" data-testid={`dedupe-empty-${entity}`}>
            <ShieldCheck className="h-8 w-8 text-emerald-500" />
            No duplicates detected — nice.
          </div>
        )}
        <div className="space-y-4">
          {groups.map((g, idx) => (
            <DuplicateGroup
              key={idx}
              entity={entity}
              group={g}
              onMerge={openMerge}
              testid={`dedupe-group-${entity}-${idx}`}
            />
          ))}
        </div>
      </CardContent>

      {pending && (
        <MergeConfirmDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          entity={entity}
          survivorId={pending.survivorId}
          loserIds={pending.losers}
          group={pending.group}
          onDone={() => {
            setMergeOpen(false);
            setPending(null);
            load();
          }}
        />
      )}
    </Card>
  );
}

function DuplicateGroup({ entity, group, onMerge, testid }) {
  const [survivorId, setSurvivorId] = useState(group.members[0]?.id);
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
            {group.count} likely duplicates
          </span>
          {group.reasons.map((r) => (
            <span key={r} className="text-[10px] font-mono uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
              {r}
            </span>
          ))}
        </div>
        <Button
          size="sm"
          onClick={() => onMerge(group, survivorId)}
          disabled={!survivorId}
          className="bg-blue-600 hover:bg-blue-700 gap-1.5"
          data-testid={`${testid}-merge-btn`}
        >
          <GitMerge className="h-3.5 w-3.5" /> Merge into selected
        </Button>
      </div>
      <div className="grid gap-2">
        {group.members.map((m) => (
          <label
            key={m.id}
            className={`flex items-start gap-3 p-2 rounded border transition ${
              survivorId === m.id ? "bg-emerald-50/70 border-emerald-300" : "bg-slate-50 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`${testid}-member-${m.id}`}
          >
            <input
              type="radio"
              name={`${testid}-survivor`}
              checked={survivorId === m.id}
              onChange={() => setSurvivorId(m.id)}
              data-testid={`${testid}-survivor-radio-${m.id}`}
              className="mt-1 h-4 w-4 accent-emerald-600"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-slate-900 truncate">
                  {entity === "account" ? m.name : `${m.first_name || ""} ${m.last_name || ""}`.trim()}
                </div>
                {survivorId === m.id && (
                  <span className="text-[10px] font-mono uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                    Survivor
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-slate-500 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
                <span className="font-mono">{m.id}</span>
                {entity === "account" && m.website && <span>Website · {m.website}</span>}
                {entity === "account" && m.owner_name && <span>Owner · {m.owner_name}</span>}
                {entity === "account" && (m.mrr != null) && <span>MRR · £{(m.mrr || 0).toLocaleString()}</span>}
                {entity === "contact" && m.email && <span>Email · {m.email}</span>}
                {entity === "contact" && (m.phone || m.mobile) && <span>Phone · {m.phone || m.mobile}</span>}
                {entity === "contact" && m.job_title && <span>Title · {m.job_title}</span>}
                {m.created_at && <span>Created · {String(m.created_at).slice(0, 10)}</span>}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function MergeConfirmDialog({ open, onOpenChange, entity, survivorId, loserIds, group, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.post("/dedupe/preview", { entity, survivor_id: survivorId, loser_ids: loserIds })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Preview failed"))
      .finally(() => setLoading(false));
  }, [open, entity, survivorId, loserIds]);

  const survivor = group.members.find((m) => m.id === survivorId);
  const displayName = (m) =>
    entity === "account" ? m.name : `${m.first_name || ""} ${m.last_name || ""}`.trim();

  const apply = async () => {
    setMerging(true);
    try {
      const r = await api.post("/dedupe/merge", { entity, survivor_id: survivorId, loser_ids: loserIds });
      const totalReparented = Object.values(r.data.reparented || {}).reduce((a, b) => a + b, 0);
      toast.success(`Merged · ${loserIds.length} loser${loserIds.length === 1 ? "" : "s"} folded in, ${totalReparented} records re-parented`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="dedupe-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4" /> Confirm merge
          </DialogTitle>
          <DialogDescription>
            The loser {loserIds.length === 1 ? "record" : "records"} will be soft-deleted (kept in the DB with a{" "}
            <span className="font-mono">merged_into</span> pointer). Every linked record moves to the survivor.
            You can undo this from the <em>Merged records</em> tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-700">Survivor</div>
            <div className="font-medium text-emerald-900">{displayName(survivor)}</div>
            <div className="text-xs font-mono text-emerald-800/70">{survivor.id}</div>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-sm space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-mono text-rose-700">
              Losers ({loserIds.length})
            </div>
            {loserIds.map((lid) => {
              const l = group.members.find((m) => m.id === lid);
              return (
                <div key={lid} className="flex items-baseline justify-between">
                  <div className="font-medium text-rose-900">{displayName(l)}</div>
                  <div className="text-[10px] font-mono text-rose-700/70">{lid}</div>
                </div>
              );
            })}
          </div>
          {loading ? (
            <div className="text-sm text-slate-500 text-center py-3">Calculating impact…</div>
          ) : preview ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm">
              <div className="text-[10px] uppercase tracking-wider font-mono text-blue-700 mb-1">
                Will re-parent to survivor
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-blue-900" data-testid="dedupe-merge-impact">
                {Object.entries(preview.will_reparent || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="capitalize">{k}</span>
                    <span className="font-mono font-semibold">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              Un-merging clears the <span className="font-mono">merged_into</span> pointer but does not un-move
              the linked records — a manual review is required after restore.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={merging}>Cancel</Button>
          <Button
            onClick={apply}
            disabled={merging || loading}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
            data-testid="dedupe-merge-confirm"
          >
            <GitMerge className="h-3.5 w-3.5" />
            {merging ? "Merging…" : "Merge now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergedList() {
  const [entity, setEntity] = useState("account");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dedupe/merged", { params: { entity } });
      setRows(r.data.rows || []);
    } catch {
      toast.error("Failed to load merged records");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [entity]);

  const restore = async (row) => {
    if (!window.confirm(`Restore this ${entity}? Its merged_into pointer will be cleared. Linked records already moved to the survivor stay where they are.`)) return;
    setRestoring(row.id);
    try {
      await api.post(`/dedupe/restore/${entity}/${row.id}`);
      toast.success("Restored");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-heading">Merged {entity}s ({rows.length})</CardTitle>
        <div className="flex gap-2">
          <Button variant={entity === "account" ? "default" : "outline"} size="sm" onClick={() => setEntity("account")} className={entity === "account" ? "bg-blue-600 hover:bg-blue-700" : ""}>Accounts</Button>
          <Button variant={entity === "contact" ? "default" : "outline"} size="sm" onClick={() => setEntity("contact")} className={entity === "contact" ? "bg-blue-600 hover:bg-blue-700" : ""}>Contacts</Button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {loading ? (
          <div className="text-sm text-slate-500 py-4 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center" data-testid="dedupe-merged-empty">
            No merged {entity}s yet.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 border border-slate-200 rounded" data-testid={`dedupe-merged-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {entity === "account" ? r.name : `${r.first_name || ""} ${r.last_name || ""}`.trim()}
                  </div>
                  <div className="text-xs text-slate-500">
                    Merged into <span className="font-mono">{r.merged_into}</span> · by {r.merged_by || "—"} · on {String(r.merged_at || "").slice(0, 10)}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => restore(r)}
                  disabled={restoring === r.id}
                  className="gap-1.5"
                  data-testid={`dedupe-restore-${r.id}`}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  {restoring === r.id ? "Restoring…" : "Un-merge"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
